import { handleResponseOld, makeGet } from './helpers';
import { BiddingInfo } from '@/types/BiddingInfo';
import { BidIncrements } from '@/types/BidIncrement';
import { BuyNow } from '@/types/BuyNow';
import { Catalog } from '@/types/Catalog';
import { CoverLots } from '@/types/CoverLot';
import { Item } from '@/types/Item';
import { Seller } from '@/types/Seller';

/**
 * @category Item API
 * @see fetchAboutData
 */
type FetchAboutDataParams = {
    deployment: string;
};
/**
 * fetches About data for a given page via Get request to Item API
 * @function fetchAboutData
 * @category Item API
 * @param {FetchAboutDataParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/spapage/about
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/react-site/routes/get-spa-about-page.ts
 */
const fetchAboutData = ({ deployment }: FetchAboutDataParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({ deployment, path: '<ITEM-API>spapage/about' });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see fetchHomeData
 */
type FetchHomeDataParams = {
    authToken: string;
    deployment: string;
    forceUpdate: boolean;
};

type FetchHomeDataResponse = {
    data: {
        acn: {
            auctionPreviews: [
                {
                    excerpt: string;
                    imageUrl: string;
                    link: string;
                    title: string;
                }
            ];
            auctionResults: [
                {
                    link: string;
                    title: string;
                }
            ];
            beInformed: {
                header: string;
                sub: [
                    {
                        link: string;
                        title: string;
                    }
                ];
            };
            topNews: [
                {
                    date: string;
                    link: string;
                    title: string;
                }
            ];
        };
        bidIncrements: BidIncrements[];
        biddingInfos: BiddingInfo[];
        buyNows: BuyNow[];
        catalogs: Catalog[];
        categories: [
            {
                imageUrl: string;
                label: string;
                link: string;
                title: string;
            }
        ];
        featuredCatalogs: number[];
        featuredCategoriesTitle: string;
        items: Item[];
        sellers: Seller[];
        siteStatistics: {
            houses: number;
            lotsDone: number;
            lotsListed: number;
            totalBidsUsd: number;
            upcomingCount: number;
        };
        smallCoverLots: CoverLots[];
        trendingItems: number[];
        upcomingCatalogs: number[];
    };
    message: string;
    success: boolean;
};

/**
 * fetches Home page data via Get request to Item API
 * @function fetchHomeData
 * @category Item API
 * @param {FetchHomeDataParams}
 * @returns {FetchHomeDataResponse}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/spapage/home
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/react-site/routes/get-spa-home-page.ts
 */
const fetchHomeData = ({ authToken, deployment, forceUpdate }: FetchHomeDataParams) =>
    new Promise<FetchHomeDataResponse>((resolve, reject) => {
        const request = makeGet({ authToken, deployment, path: '<ITEM-API>spapage/home' });
        const query: any = {};
        query.bust = true;
        query.trendingLimit = 20;

        if (forceUpdate) {
            query.bustLocalCache = Date.now();
        }
        request.query(query);
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

export default {
    fetchAboutData,
    fetchHomeData,
};
